import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Pagination,
    Card,
    CardContent,
    Grid,
    IconButton,
    Tooltip,
    Alert,
    Snackbar,
    CircularProgress,
    Divider,
} from '@mui/material';
import {
    Visibility as VisibilityIcon,
    CheckCircle as ApproveIcon,
    Cancel as RejectIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import api from '../services/api';

const FundingApprovals = () => {
    const theme = useTheme();
    const { isDarkMode } = useCustomTheme();
    const [fundingRequests, setFundingRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState('pending');

    const [selectedRequest, setSelectedRequest] = useState(null);
    const [detailsDialog, setDetailsDialog] = useState(false);
    const [approveDialog, setApproveDialog] = useState(false);
    const [rejectDialog, setRejectDialog] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [processing, setProcessing] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const fetchFundingRequests = useCallback(async () => {
        try {
            setLoading(true);
            const data = await api.dashboard.getFundingRequests(status);
            if (data.success) {
                setFundingRequests(data.data);
            }
        } catch (error) {
            console.error('Error fetching funding requests:', error);
            showSnackbar('Failed to fetch funding requests', 'error');
        } finally {
            setLoading(false);
        }
    }, [status]);

    useEffect(() => {
        fetchFundingRequests();
    }, [fetchFundingRequests]);

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleApprove = async () => {
        try {
            setProcessing(true);
            const response = await api.dashboard.approveFundingRequest(selectedRequest._id);
            if (response.success) {
                showSnackbar('Funding request approved and executed successfully');
                setApproveDialog(false);
                fetchFundingRequests();
            }
        } catch (error) {
            console.error('Error approving request:', error);
            showSnackbar(error.message || 'Failed to approve request', 'error');
        } finally {
            setProcessing(false);
        }
    };

    const handleReject = async () => {
        if (!rejectionReason) return;
        try {
            setProcessing(true);
            const response = await api.dashboard.rejectFundingRequest(selectedRequest._id, rejectionReason);
            if (response.success) {
                showSnackbar('Funding request rejected successfully');
                setRejectDialog(false);
                setRejectionReason('');
                fetchFundingRequests();
            }
        } catch (error) {
            console.error('Error rejecting request:', error);
            showSnackbar(error.message || 'Failed to reject request', 'error');
        } finally {
            setProcessing(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending': return 'warning';
            case 'approved': return 'success';
            case 'rejected': return 'error';
            default: return 'default';
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-NG', {
            style: 'currency',
            currency: 'NGN',
        }).format(amount);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString('en-NG', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" sx={{ color: theme.palette.text.primary }}>
                    Funding Approvals
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant={status === 'pending' ? 'contained' : 'outlined'}
                        onClick={() => setStatus('pending')}
                    >
                        Pending
                    </Button>
                    <Button
                        variant={status === 'approved' ? 'contained' : 'outlined'}
                        onClick={() => setStatus('approved')}
                    >
                        Approved
                    </Button>
                    <Button
                        variant={status === 'rejected' ? 'contained' : 'outlined'}
                        onClick={() => setStatus('rejected')}
                    >
                        Rejected
                    </Button>
                    <IconButton onClick={fetchFundingRequests} sx={{ ml: 1 }}>
                        <RefreshIcon />
                    </IconButton>
                </Box>
            </Box>

            <Paper sx={{ backgroundColor: theme.palette.background.paper }}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Admin (Requester)</TableCell>
                                <TableCell>User (Recipient)</TableCell>
                                <TableCell>Amount</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center"><CircularProgress /></TableCell>
                                </TableRow>
                            ) : fundingRequests.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        <Typography variant="body1" sx={{ color: theme.palette.text.secondary, py: 3 }}>
                                            No funding requests found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                fundingRequests.map((request) => (
                                    <TableRow key={request._id}>
                                        <TableCell>
                                            <Typography variant="body2">{request.adminId?.fullname || 'N/A'}</Typography>
                                            <Typography variant="caption" color="textSecondary">@{request.adminId?.username}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{request.userId?.fullname || 'N/A'}</Typography>
                                            <Typography variant="caption" color="textSecondary">@{request.userId?.username}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                {formatCurrency(request.amount)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={request.status} color={getStatusColor(request.status)} size="small" />
                                        </TableCell>
                                        <TableCell>{formatDate(request.createdAt)}</TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Tooltip title="View Details">
                                                    <IconButton size="small" onClick={() => { setSelectedRequest(request); setDetailsDialog(true); }}>
                                                        <VisibilityIcon color="primary" />
                                                    </IconButton>
                                                </Tooltip>
                                                {request.status === 'pending' && (
                                                    <>
                                                        <Tooltip title="Approve">
                                                            <IconButton size="small" onClick={() => { setSelectedRequest(request); setApproveDialog(true); }}>
                                                                <ApproveIcon color="success" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Reject">
                                                            <IconButton size="small" onClick={() => { setSelectedRequest(request); setRejectDialog(true); }}>
                                                                <RejectIcon color="error" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Details Dialog */}
            <Dialog open={detailsDialog} onClose={() => setDetailsDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Funding Request Details</DialogTitle>
                <DialogContent dividers>
                    {selectedRequest && (
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" color="textSecondary">Requester (Admin)</Typography>
                                <Typography variant="body1">{selectedRequest.adminId?.fullname} (@{selectedRequest.adminId?.username})</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" color="textSecondary">Recipient (User)</Typography>
                                <Typography variant="body1">{selectedRequest.userId?.fullname} (@{selectedRequest.userId?.username})</Typography>
                                <Typography variant="caption">{selectedRequest.userId?.email}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" color="textSecondary">Amount</Typography>
                                <Typography variant="h5" color="primary">{formatCurrency(selectedRequest.amount)}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" color="textSecondary">Narration</Typography>
                                <Typography variant="body1">{selectedRequest.narration || 'No narration provided'}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                                <Chip label={selectedRequest.status} color={getStatusColor(selectedRequest.status)} />
                            </Grid>
                            {selectedRequest.status === 'approved' && (
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="textSecondary">Approved By</Typography>
                                    <Typography variant="body1">{selectedRequest.approvedBy?.fullname}</Typography>
                                </Grid>
                            )}
                            {selectedRequest.status === 'rejected' && (
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="textSecondary">Rejection Reason</Typography>
                                    <Typography variant="body1" color="error">{selectedRequest.rejectionReason}</Typography>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailsDialog(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Approve Dialog */}
            <Dialog open={approveDialog} onClose={() => setApproveDialog(false)}>
                <DialogTitle>Approve Funding</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to approve this funding request of <b>{selectedRequest && formatCurrency(selectedRequest.amount)}</b> for <b>{selectedRequest?.userId?.fullname}</b>?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setApproveDialog(false)}>Cancel</Button>
                    <Button onClick={handleApprove} variant="contained" color="success" disabled={processing}>
                        {processing ? <CircularProgress size={24} /> : 'Confirm Approve'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Reject Dialog */}
            <Dialog open={rejectDialog} onClose={() => setRejectDialog(false)}>
                <DialogTitle>Reject Funding</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>Please provide a reason for rejection:</Typography>
                    <TextField
                        fullWidth
                        label="Rejection Reason"
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        required
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectDialog(false)}>Cancel</Button>
                    <Button onClick={handleReject} variant="contained" color="error" disabled={processing || !rejectionReason}>
                        {processing ? <CircularProgress size={24} /> : 'Reject Request'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default FundingApprovals;
